/**
 * Tapo Camera Finder — multi-step wizard + product matching engine
 * Power profiles: Kabelgebunden, Kabelgebunden PoE, and Kabelgebunden DC are separate tokens.
 */
(function () {
  if (window.customElements.get('camera-finder')) return;

  const PLACEHOLDER_IMAGE =
    'data:image/svg+xml,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="#f3f4f6" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="sans-serif" font-size="14">No image</text></svg>'
    );

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function translationOrFallback(value, fallback) {
    const text = String(value ?? '').trim();
    if (!text || text.includes('Translation missing')) return fallback;
    return text;
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const amount = Number(cents);
    if (Number.isNaN(amount)) return '';

    const format =
      moneyFormat ||
      window.themeVariables?.settings?.moneyFormat ||
      window.themeVariables?.settings?.moneyWithCurrencyFormat;

    if (typeof window.formatMoney === 'function' && format) {
      return window.formatMoney(amount, format);
    }

    return (amount / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'EUR',
    });
  }

  function productImage(product) {
    return product?.image || PLACEHOLDER_IMAGE;
  }

  function productSavePct(product) {
    const price = Number(product?.price);
    const compareAt = Number(product?.compareAtPrice);
    if (product?.savePct > 0) return product.savePct;
    if (!compareAt || compareAt <= price) return 0;
    return Math.round((1 - price / compareAt) * 100);
  }

  function normalizeDisplayText(value) {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) {
      const items = value.map((item) => String(item ?? '').trim()).filter(Boolean);
      return items.find((item) => item !== 'Value Set') || '';
    }
    const text = String(value).trim();
    if (!text || text === 'Value Set' || text === '["Value Set"]') return '';
    return text;
  }

  function productHasCtsBadges(product, options = {}) {
    if (options.showRecommend) return true;
    if (product?.isBestseller) return true;
    if (productSavePct(product) > 0) return true;
    if (normalizeDisplayText(product?.promoBadge)) return true;
    return false;
  }

  function renderCtsBadges(product, options = {}) {
    const lines = [];

    if (options.showRecommend) {
      lines.push(
        `<span class="cts-badge cts-badge--recommend">${formatMultilineText(options.recommendLabel || 'Beste Empfehlung')}</span>`
      );
    }

    if (product?.isBestseller) {
      lines.push(`<span class="cts-badge cts-badge--best">Bestseller</span>`);
    }

    const savePct = productSavePct(product);
    if (savePct > 0) {
      lines.push(`<span class="cts-badge cts-badge--sale">${savePct}% Rabatt</span>`);
    }

    const promoBadge = normalizeDisplayText(product?.promoBadge);
    if (promoBadge) {
      lines.push(`<span class="cts-badge cts-badge--promo">${escapeHtml(promoBadge)}</span>`);
    }

    if (!lines.length) return '';
    return `<div class="cts-badges cf-cts-badges--stack">${lines.join('')}</div>`;
  }

  function renderFeatureTags(product) {
    const items = (product?.features || [])
      .map((item) => normalizeDisplayText(item))
      .filter(Boolean);
    if (!items.length) return '';

    return `<div class="cf-selection-tags">${items
      .map((item) => `<span class="cf-selection-tag">${escapeHtml(item)}</span>`)
      .join('')}</div>`;
  }

  function renderOptionText(step, option) {
    const label = String(option?.label ?? '').trim();
    const description = String(option?.description ?? '').trim();
    const isResolution = step?.id === 'finder_resolution';

    if (isResolution) {
      const parts = [];
      if (label) parts.push(`<span class="cf-option__label">${formatMultilineText(label)}</span>`);
      if (description) {
        parts.push(`<span class="cf-option__description">${formatMultilineText(description)}</span>`);
      }
      return parts.join('');
    }

    if (!label) return '';
    return `<span class="cf-option__label">${formatMultilineText(label)}</span>`;
  }

  function formatMultilineText(text) {
    return escapeHtml(String(text ?? '')).replace(/\n/g, '<br>');
  }

  function resolutionOptionImage(option, locationAnswer) {
    const loc = String(locationAnswer ?? '').trim().toLowerCase();
    const fallback = String(option?.image ?? '').trim();
    const indoor = String(option?.imageIndoor ?? '').trim() || fallback;
    const outdoor = String(option?.imageOutdoor ?? '').trim() || fallback;
    if (loc === 'outdoor') return outdoor || indoor;
    return indoor || outdoor;
  }

  function tileOptionImage(step, option, locationAnswer) {
    if (step?.id === 'finder_resolution') {
      return resolutionOptionImage(option, locationAnswer);
    }
    return String(option?.image ?? '').trim();
  }

  function renderResultsProgressBar() {
    return `
      <div class="cf-step-progress cf-results-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100" aria-label="Quiz progress">
        <div class="cf-step-progress__fill" style="width: 100%"></div>
      </div>`;
  }

  function bannerBackgroundStyle(imageUrl) {
    const safeUrl = String(imageUrl).replace(/\\/g, '%5C').replace(/'/g, '%27');
    return ` style="background-image: url('${safeUrl}')"`;
  }

  function renderResultBanner(banner) {
    if (!banner) return '';
    const hasContent = banner.image || banner.heading || banner.subheading;
    if (!hasContent) return '';

    const tag = banner.url ? 'a' : 'div';
    const hrefAttr = banner.url ? ` href="${escapeHtml(banner.url)}"` : '';
    const imageClass = banner.image ? ' cf-results-banner--has-image' : ' cf-results-banner--placeholder';
    const styleAttr = banner.image ? bannerBackgroundStyle(banner.image) : '';

    return `<${tag} class="cf-results-banner${imageClass}"${hrefAttr}${styleAttr}>
      <div class="cf-results-banner__content">
        ${banner.heading ? `<h3 class="cf-results-banner__heading">${formatMultilineText(banner.heading)}</h3>` : ''}
        ${banner.subheading ? `<p class="cf-results-banner__subheading">${formatMultilineText(banner.subheading)}</p>` : ''}
      </div>
    </${tag}>`;
  }

  function renderResultBanners(banners) {
    const items = (banners || []).map(renderResultBanner).filter(Boolean);
    if (!items.length) return '';
    return `<div class="cf-results-banners">${items.join('')}</div>`;
  }

  async function fetchProductInfoText(productUrl) {
    try {
      const url = new URL(productUrl, window.location.origin).href;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return '';

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const prose = doc.querySelector('.product-info__text .prose');
      return prose?.innerHTML?.trim() || '';
    } catch {
      return '';
    }
  }

  function renderWishlistButton(product, options = {}) {
    const ariaLabel = escapeHtml(options.ariaLabel || 'Zur Wunschliste hinzufügen');
    const iconColor = escapeHtml(options.iconColor || '#828282');
    const inlineClass = options.inline ? ' cf-wishlist-inline' : '';

    const buttonHtml = `<button
      type="button"
      class="swym-advanced-wishlist-collections${inlineClass}"
      data-wishlist-toggle
      data-product-id="${Number(product.id)}"
      data-variant-id="${Number(product.variantId)}"
      aria-label="${ariaLabel}"
      aria-pressed="false"
      style="color:${iconColor};">
      <div class="swym-advanced-wishlist-collections-social-count"></div>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M19.6706 5.4736C17.6806 3.8336 14.7206 4.1236 12.8906 5.9536L12.0006 6.8436L11.1106 5.9536C9.29063 4.1336 6.32064 3.8336 4.33064 5.4736C2.05064 7.3536 1.93063 10.7436 3.97063 12.7836L11.6406 20.4536C11.8406 20.6536 12.1506 20.6536 12.3506 20.4536L20.0206 12.7836C22.0706 10.7436 21.9506 7.3636 19.6706 5.4736Z" stroke="${iconColor}" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    </button>`;

    if (options.corner) {
      return `<div class="swym-advanced-wishlist-collections-container cf-wishlist-corner">${buttonHtml}</div>`;
    }

    return buttonHtml;
  }

  function renderProductMedia(product, badgeOptions = {}) {
    const badgesHtml = productHasCtsBadges(product, badgeOptions)
      ? renderCtsBadges(product, badgeOptions)
      : '';
    const wishlistHtml = badgeOptions.wishlistCorner
      ? renderWishlistButton(product, { corner: true, ariaLabel: badgeOptions.wishlistAriaLabel })
      : '';

    return `
      <div class="cf-product-media cts-card__image-wrap">
        ${badgesHtml}
        ${wishlistHtml}
        <a href="${escapeHtml(product.url)}" class="cf-product-media__link${badgesHtml ? '' : ' cf-product-media__link--no-badges'}">
          <img src="${escapeHtml(productImage(product))}" alt="${escapeHtml(product.title)}" loading="lazy" class="cf-product-media__img">
        </a>
      </div>`;
  }

  function onSwymReady(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  function swymProductPayload(product) {
    return {
      empi: Number(product.id),
      epi: Number(product.variantId),
      du: new URL(product.url, window.location.origin).href,
    };
  }

  function flattenSwymListItems(lists) {
    if (!lists) return [];
    if (!Array.isArray(lists)) return [];

    const items = [];
    lists.forEach((entry) => {
      if (Array.isArray(entry?.listcontents)) {
        items.push(...entry.listcontents);
      } else if (entry?.epi != null || entry?.empi != null) {
        items.push(entry);
      }
    });
    return items;
  }

  function isInSwymWishlist(items, payload) {
    return items.some(
      (item) => Number(item.epi) === payload.epi || Number(item.empi) === payload.empi
    );
  }

  function setWishlistButtonState(button, active) {
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const path = button.querySelector('svg path');
    if (path) {
      const iconColor = button.style.color || '#828282';
      path.setAttribute('fill', active ? iconColor : 'none');
      path.setAttribute('stroke', iconColor);
    }
  }

  function fetchSwymWishlistItems(swat, callback) {
    if (typeof swat.fetch === 'function') {
      swat.fetch((response) => callback(flattenSwymListItems(response)));
      return;
    }

    if (typeof swat.fetchLists === 'function') {
      swat.fetchLists({}, (response) => {
        const lists = response?.lists || response || [];
        callback(flattenSwymListItems(lists));
      });
      return;
    }

    callback([]);
  }

  function syncWishlistButton(swat, product, button) {
    const payload = swymProductPayload(product);
    fetchSwymWishlistItems(swat, (items) => {
      if (button.isConnected) {
        setWishlistButtonState(button, isInSwymWishlist(items, payload));
      }
    });
  }

  function toggleSwymWishlist(swat, product, button) {
    const payload = swymProductPayload(product);
    const isActive = button.classList.contains('is-active');
    const action = isActive ? 'removeFromWishList' : 'addToWishList';

    if (typeof swat[action] !== 'function') return Promise.resolve();

    button.disabled = true;

    return new Promise((resolve) => {
      swat[action](payload, () => {
        if (button.isConnected) {
          setWishlistButtonState(button, !isActive);
          button.disabled = false;
        }
        resolve();
      });
    });
  }

  function restartLabel(strings) {
    return translationOrFallback(strings?.restart, 'Neustarten');
  }

  function repeatButtonLabel(strings, storeLocale) {
    const fromStrings = translationOrFallback(strings?.startNew, '');
    if (fromStrings) return fromStrings;

    const locale = String(storeLocale || document.documentElement.getAttribute('lang') || 'de').toLowerCase();
    return locale.startsWith('en') ? 'Start New' : 'Neustarten';
  }

  function repeatButtonLang(storeLocale) {
    const locale = String(storeLocale || document.documentElement.getAttribute('lang') || 'de').toLowerCase();
    return locale.startsWith('en') ? 'en' : 'de';
  }

  function setButtonLabel(button, text) {
    if (!button) return;
    const labelEl = button.querySelector('.cf-btn__label');
    if (labelEl) {
      labelEl.textContent = text;
    } else {
      button.textContent = text;
    }
  }

  function updateRestartButtonLabel(strings, button) {
    setButtonLabel(button, restartLabel(strings));
  }

  function updateRepeatButtonLabel(strings, storeLocale, button) {
    setButtonLabel(button, repeatButtonLabel(strings, storeLocale));
  }

  function themeProductString(strings, key, themeKey, fallback) {
    if (strings?.[key]?.trim()) return strings[key].trim();
    const themeStrings = window.themeVariables?.strings || {};
    if (themeStrings[themeKey]?.trim()) return themeStrings[themeKey].trim();
    return fallback;
  }

  function normalizePowerNetworkToken(token) {
    const trimmed = String(token ?? '').trim().toLowerCase();
    if (trimmed === 'kabelgebunden dc') return 'kabelgebunden_dc';
    if (trimmed === 'kabelgebunden poe') return 'kabelgebunden_poe';
    if (trimmed === 'kabelgebunden') return 'kabelgebunden';
    return trimmed;
  }

  const POWER_PROFILES = [
    {
      id: 'wired_wlan',
      label: 'Kabelgebunden + WLAN',
      match: (values) => values.has('kabelgebunden') && values.has('wlan'),
    },
    {
      id: 'poe_dc_flex',
      label: 'Kabelgebunden PoE/DC + Flexibles Netzwerk (Ethernet/WLAN)',
      match: (values) =>
        (values.has('kabelgebunden_poe') || values.has('kabelgebunden_dc')) &&
        (values.has('ethernet') || values.has('wlan')),
    },
    {
      id: 'poe_dc_4g',
      label: 'Kabelgebunden PoE/DC + 4G (Sim-Karte)',
      match: (values) =>
        (values.has('kabelgebunden_poe') || values.has('kabelgebunden_dc')) && values.has('4g'),
    },
    {
      id: 'akku_wlan',
      label: 'Akku + WLAN',
      match: (values) =>
        values.has('akku') && values.has('wlan') && !values.has('solarpanel'),
    },
    {
      id: 'solar_wlan',
      label: 'Akku mit Solarpanel + WLAN',
      match: (values) => values.has('akku') && values.has('solarpanel') && values.has('wlan'),
    },
    {
      id: 'solar_4g',
      label: 'Akku mit Solarpanel + 4G (Sim-Karte)',
      match: (values) => values.has('akku') && values.has('solarpanel') && values.has('4g'),
    },
  ];

  function splitMetafieldToken(token) {
    return String(token ?? '')
      .split(/\s*(?:•|·|\||,|\+)\s*/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  function normalizeAttributeValue(value) {
    const raw = Array.isArray(value)
      ? value
      : value == null || value === ''
        ? []
        : [value];
    return raw.flatMap((item) => splitMetafieldToken(item));
  }

  function powerNetworkValues(product) {
    return new Set(
      normalizeAttributeValue(product.attributes?.finder_power_network).map(normalizePowerNetworkToken)
    );
  }

  function productPowerProfiles(product) {
    const values = powerNetworkValues(product);
    return POWER_PROFILES.filter((profile) => profile.match(values)).map((profile) => profile.id);
  }

  function normalizeLocation(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  function locationKameraMatches(productLocations, answer) {
    const selected = normalizeLocation(answer);
    if (!selected) return false;

    const locations = normalizeAttributeValue(productLocations).map((value) => normalizeLocation(value));
    if (!locations.length) return false;

    return locations.includes(selected);
  }

  function attributeMatches(product, step, answer) {
    if (answer == null || answer === '') return false;

    const stepId = step.id;
    const attrs = product.attributes || {};

    if (stepId === 'finder_location_kamera') {
      return locationKameraMatches(attrs.finder_location_kamera, answer);
    }

    if (stepId === 'finder_power_network') {
      return productPowerProfiles(product).includes(String(answer).trim());
    }

    if (stepId === 'finder_resolution') {
      const productValues = normalizeAttributeValue(attrs.finder_resolution).map((value) =>
        value.toLowerCase()
      );
      return productValues.includes(String(answer).trim().toLowerCase());
    }

    const productValues = normalizeAttributeValue(attrs[stepId]);
    if (!productValues.length) return false;
    return productValues.includes(String(answer).trim());
  }

  function filterProductsByPriorAnswers(products, steps, answers, stepIndex) {
    return products.filter((product) => {
      for (let i = 0; i < stepIndex; i += 1) {
        const step = steps[i];
        const answer = answers[step.id];
        if (answer == null || answer === '') return false;
        if (!attributeMatches(product, step, answer)) return false;
      }
      return true;
    });
  }

  function productHasOption(product, step, optionValue) {
    return attributeMatches(product, step, optionValue);
  }

  function getOptionsForStep(products, steps, answers, stepIndex) {
    const step = steps[stepIndex];
    if (!step) return [];

    const pool = stepIndex >= 2 ? products.filter(isInStock) : products;
    const filtered = filterProductsByPriorAnswers(pool, steps, answers, stepIndex);

    if (Array.isArray(step.options) && step.options.length) {
      return step.options.map((option) => ({
        ...option,
        available: filtered.some((product) => productHasOption(product, step, option.value)),
      }));
    }

    const seen = new Set();
    const options = [];

    filtered.forEach((product) => {
      normalizeAttributeValue(product.attributes?.[step.id]).forEach((value) => {
        if (seen.has(value)) return;
        seen.add(value);
        options.push({ value, label: value, available: true });
      });
    });

    return options.sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }

  function countMatchingSteps(product, answers, steps) {
    if (!product?.attributes || !steps?.length) return 0;

    return steps.reduce((count, step) => {
      const answer = answers[step.id];
      if (answer == null || answer === '') return count;
      return attributeMatches(product, step, answer) ? count + 1 : count;
    }, 0);
  }

  /** All quiz answers must match product metafield values exactly. */
  function productMatchesAll(product, answers, steps) {
    if (!product?.attributes || !steps?.length) return false;
    return countMatchingSteps(product, answers, steps) === steps.length;
  }

  const MAX_ALTERNATIVES = 3;

  function sortByPriceDesc(a, b) {
    return Number(b.price) - Number(a.price);
  }

  function sortByPriceAsc(a, b) {
    return Number(a.price) - Number(b.price);
  }

  function isInStock(product) {
    return product?.available === true;
  }

  function pickAlternatives(products, answers, steps, recommended) {
    const totalSteps = steps.length;
    const usedIds = new Set([recommended.id]);
    const alternatives = [];

    const exactOthers = products
      .filter((product) => product.id !== recommended.id && productMatchesAll(product, answers, steps))
      .sort(sortByPriceAsc);

    for (const product of exactOthers) {
      if (alternatives.length >= MAX_ALTERNATIVES) break;
      alternatives.push(product);
      usedIds.add(product.id);
    }

    if (alternatives.length < MAX_ALTERNATIVES) {
      const partialRanked = products
        .filter((product) => !usedIds.has(product.id))
        .map((product) => ({
          product,
          score: countMatchingSteps(product, answers, steps),
        }))
        .filter((entry) => entry.score > 0 && entry.score < totalSteps)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return Number(a.product.price) - Number(b.product.price);
        });

      for (const entry of partialRanked) {
        if (alternatives.length >= MAX_ALTERNATIVES) break;
        alternatives.push(entry.product);
        usedIds.add(entry.product.id);
      }
    }

    if (alternatives.length < MAX_ALTERNATIVES) {
      const bestsellers = products
        .filter((product) => product.isBestseller && !usedIds.has(product.id))
        .sort(sortByPriceAsc);

      for (const product of bestsellers) {
        if (alternatives.length >= MAX_ALTERNATIVES) break;
        alternatives.push(product);
        usedIds.add(product.id);
      }
    }

    return alternatives.slice(0, MAX_ALTERNATIVES);
  }

  function matchProducts(products, answers, steps) {
    const inStockProducts = products.filter(isInStock);
    const exactRanked = inStockProducts
      .filter((product) => productMatchesAll(product, answers, steps))
      .sort(sortByPriceDesc);

    const recommended = exactRanked[0] || null;
    if (!recommended) {
      return { recommended: null, alternatives: [] };
    }

    const alternatives = pickAlternatives(inStockProducts, answers, steps, recommended);

    return { recommended, alternatives };
  }

  class CameraFinder extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-camera-finder-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch {
        return;
      }

      this.storeLocale = this.config.locale || document.documentElement.getAttribute('lang') || 'de';
      this.products = this.config.products || [];
      this.strings = this.config.strings || {};
      this.labels = this.config.labels || {};
      this.banners = this.config.banners || [];
      this.steps = this.config.steps || this.config.stepDefs || [];
      this.moneyFormat = this.config.moneyFormat;
      this.replayIconUrl = this.getAttribute('data-replay-icon') || this.config.replayIcon || '';
      this.currentStep = 0;
      this.answers = {};

      this.wizardEl = this.querySelector('[data-wizard]');
      this.resultsEl = this.querySelector('[data-results]');
      this.stepContainer = this.querySelector('[data-step-container]');
      this.navEl = this.querySelector('.cf-nav');
      this.backBtn = this.querySelector('[data-back]');

      this._onBack = () => this.goBack();

      this.backBtn?.addEventListener('click', this._onBack);

      this.renderStep();
      this.updateNav();
    }

    disconnectedCallback() {
      this.backBtn?.removeEventListener('click', this._onBack);
    }

    stepProgressPct() {
      const totalSteps = this.steps.length;
      if (!totalSteps) return 0;
      return ((this.currentStep + 1) / (totalSteps + 1)) * 100;
    }

    stepProgressMarkup() {
      const pct = this.stepProgressPct();

      return `
        <div class="cf-step-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}" aria-label="Quiz progress">
          <div class="cf-step-progress__fill" style="width: ${pct}%"></div>
        </div>`;
    }

    getStepOptions(stepIndex) {
      return getOptionsForStep(this.products, this.steps, this.answers, stepIndex);
    }

    clearAnswersFromStep(stepIndex) {
      for (let i = stepIndex + 1; i < this.steps.length; i += 1) {
        delete this.answers[this.steps[i].id];
      }
    }

    setAnswer(stepIndex, value) {
      const step = this.steps[stepIndex];
      if (!step) return;

      this.answers[step.id] = value;
      this.clearAnswersFromStep(stepIndex);
      this.updateNav();
    }

    renderStep() {
      const step = this.steps[this.currentStep];
      if (!step || !this.stepContainer) return;

      const options = this.getStepOptions(this.currentStep);
      let selected = this.answers[step.id];
      const selectedOption = options.find((opt) => opt.value === selected);

      if (selected != null && (!selectedOption || !selectedOption.available)) {
        delete this.answers[step.id];
        selected = undefined;
      }

      if (options.length === 0) {
        this.stepContainer.innerHTML = `
          <h2 class="cf-step__title">${escapeHtml(step.question)}</h2>
          ${this.stepProgressMarkup()}
          <p class="cf-empty">${escapeHtml('Keine passenden Optionen für deine bisherige Auswahl.')}</p>`;
        this.stepContainer.focus({ preventScroll: true });
        return;
      }

      if (step.type === 'tiles') {
        const tileClass =
          step.id === 'finder_resolution'
            ? 'cf-options cf-options--tiles cf-options--resolution'
            : step.id === 'finder_location_kamera'
              ? 'cf-options cf-options--tiles cf-options--location'
              : 'cf-options cf-options--tiles cf-options--power';

        this.stepContainer.innerHTML = `
          <h2 class="cf-step__title">${escapeHtml(step.question)}</h2>
          ${this.stepProgressMarkup()}
          <div class="${tileClass}" role="radiogroup" aria-label="${escapeHtml(step.question)}">
            ${options
              .map((opt) => {
                const unavailable = opt.available === false;
                const tileImage = tileOptionImage(step, opt, this.answers.finder_location_kamera);
                return `
              <button type="button" class="cf-option${selected === opt.value ? ' is-selected' : ''}${unavailable ? ' is-unavailable' : ''}"
                data-value="${escapeHtml(opt.value)}" role="radio" aria-checked="${selected === opt.value}"
                ${unavailable ? 'disabled aria-disabled="true"' : ''}>
                ${tileImage ? `<span class="cf-option__image"><img src="${escapeHtml(tileImage)}" alt="" loading="lazy"></span>` : opt.icon ? `<span class="cf-option__icon">${opt.icon}</span>` : ''}
                  <span class="cf-option__text">${renderOptionText(step, opt)}</span>
              </button>`;
              })
              .join('')}
          </div>`;

        this.stepContainer.querySelectorAll('.cf-option:not(.is-unavailable)').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.setAnswer(this.currentStep, btn.dataset.value);
            this.stepContainer.querySelectorAll('.cf-option').forEach((b) => {
              b.classList.toggle('is-selected', b === btn);
              b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
            });
            this.advanceAfterAnswer();
          });
        });
      } else if (step.type === 'list') {
        this.stepContainer.innerHTML = `
          <h2 class="cf-step__title">${escapeHtml(step.question)}</h2>
          ${this.stepProgressMarkup()}
          <div class="cf-options cf-options--list" role="radiogroup" aria-label="${escapeHtml(step.question)}">
            ${options
              .map((opt) => {
                const unavailable = opt.available === false;
                return `
              <label class="cf-radio${selected === opt.value ? ' is-selected' : ''}${unavailable ? ' is-unavailable' : ''}">
                <input type="radio" name="cf-${escapeHtml(step.id)}" value="${escapeHtml(opt.value)}"${selected === opt.value ? ' checked' : ''}${unavailable ? ' disabled' : ''}>
                <span class="cf-radio__mark"></span>
                <span class="cf-radio__label">${escapeHtml(opt.label)}</span>
              </label>`;
              })
              .join('')}
          </div>`;

        this.stepContainer.querySelectorAll('input[type="radio"]:not(:disabled)').forEach((input) => {
          input.addEventListener('change', () => {
            this.setAnswer(this.currentStep, input.value);
            this.stepContainer.querySelectorAll('.cf-radio').forEach((label) => {
              label.classList.toggle('is-selected', label.querySelector('input') === input);
            });
            this.advanceAfterAnswer();
          });
        });
      }

      this.stepContainer.focus({ preventScroll: true });
    }

    updateNav() {
      if (this.backBtn) {
        this.backBtn.hidden = this.currentStep === 0;
      }
    }

    advanceAfterAnswer() {
      const step = this.steps[this.currentStep];
      if (!step || this.answers[step.id] == null) return;

      if (this.currentStep < this.steps.length - 1) {
        this.currentStep += 1;
        this.renderStep();
        this.updateNav();
        return;
      }

      this.showResults();
    }

    goBack() {
      if (this.currentStep === 0) return;
      this.currentStep -= 1;
      this.renderStep();
      this.updateNav();
    }

    setQuizVisible(visible) {
      if (this.stepContainer) this.stepContainer.hidden = !visible;
      if (this.navEl) this.navEl.hidden = !visible;
      if (this.wizardEl) this.wizardEl.hidden = false;
    }

    resetToWizard() {
      this.currentStep = 0;
      this.answers = {};

      if (this.resultsEl) {
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
      }

      this.setQuizVisible(true);
      this.renderStep();
      this.updateNav();
    }

    showResults() {
      const { recommended, alternatives } = matchProducts(this.products, this.answers, this.steps);
      this._resultProduct = recommended || null;

      this.setQuizVisible(false);

      if (this.resultsEl) {
        this.resultsEl.hidden = false;
        this.resultsEl.innerHTML = this.buildResultsHtml(recommended, alternatives);
        this.bindResultsActions();
        this.loadHeroProductInfoText(recommended);
      }

      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    buildResultsHtml(recommended, alternatives) {
      if (!recommended) {
        return `
          <div class="cf-info" role="status" aria-live="polite">
            <div class="cf-info__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 8v5M12 16h.01" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="cf-info__body">
              <span class="cf-info__label">${formatMultilineText(this.labels.noResultsInfo || 'Information')}</span>
              <p class="cf-info__message">${formatMultilineText(this.labels.noResults || 'No suitable camera found. Please adjust your selection.')}</p>
              <button type="button" class="cf-btn cf-btn--primary" data-restart>
                ${escapeHtml(restartLabel(this.strings))}
              </button>
            </div>
          </div>`;
      }

      const featureTags = renderFeatureTags(recommended);
      const compareHtml =
        recommended.compareAtPrice > recommended.price
          ? `<s class="cf-hero__compare">${formatMoney(recommended.compareAtPrice, this.moneyFormat)}</s>`
          : '';

      const resultsHeading = formatMultilineText(
        this.labels.resultsHeading || 'Wir haben deine ideale Sicherheitskamera gefunden!'
      );

      const restartIcon = (this.replayIconUrl || '').trim();
      const restartIconHtml = restartIcon
        ? `<img src="${escapeHtml(restartIcon)}" alt="" class="cf-btn__icon-img" width="18" height="18" loading="lazy">`
        : '';
      const restartText = repeatButtonLabel(this.strings, this.storeLocale);
      const restartLang = repeatButtonLang(this.storeLocale);
      const addToCartLabel = themeProductString(this.strings, 'addToCart', 'addToCartButton', 'In den Warenkorb');
      const soldOutLabel = themeProductString(this.strings, 'soldOut', 'soldOutButton', 'Ausverkauft');
      const viewProductLabel = themeProductString(this.strings, 'viewProduct', 'viewProduct', 'Produkt ansehen');

      const altHtml = (alternatives || [])
        .filter(Boolean)
        .map(
          (p) => `
        <article class="cf-alt-card">
          ${renderProductMedia(p, {
            wishlistCorner: true,
            wishlistAriaLabel: this.labels.addToWishlist || 'Zur Wunschliste hinzufügen',
          })}
          <div class="cf-alt-card__body">
            <h4 class="cf-alt-card__title"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h4>
            <p class="cf-alt-card__price">${formatMoney(p.price, this.moneyFormat)}</p>
            <button type="button" class="cf-btn cf-btn--primary cf-btn--block" data-add-to-cart="${p.variantId}"${p.available ? '' : ' disabled'}>
              ${escapeHtml(p.available ? addToCartLabel : soldOutLabel)}
            </button>
          </div>
        </article>`
        )
        .join('');

      return `
        <div class="cf-results">
          <div class="cf-results__header">
            <h2 class="cf-results__title">${resultsHeading}</h2>
            <button type="button" class="cf-btn cf-btn--repeat" data-restart>
              ${restartIconHtml}
              <span class="cf-btn__label" lang="${restartLang}">${escapeHtml(restartText)}</span>
            </button>
          </div>

          ${renderResultsProgressBar()}

          <article class="cf-hero">
            <div class="cf-hero__inner">
              ${renderProductMedia(recommended, {
                showRecommend: true,
                recommendLabel: this.labels.bestMatch || 'Beste Empfehlung',
              })}
              <div class="cf-hero__body">
                <h3 class="cf-hero__title"><a href="${escapeHtml(recommended.url)}">${escapeHtml(recommended.title)}</a></h3>
                ${featureTags}
                <div class="product-info__text cf-hero__product-info cf-hero__product-info--pending" data-hero-product-info>
                  <div class="prose" data-hero-product-info-prose></div>
                </div>
                <div class="cf-hero__price-row">
                  <p class="cf-hero__price">${formatMoney(recommended.price, this.moneyFormat)}</p>
                  ${compareHtml}
                </div>
                <div class="cf-hero__actions">
                  <a href="${escapeHtml(recommended.url)}" class="cf-btn cf-btn--primary">${escapeHtml(viewProductLabel)}</a>
                  <div class="cf-hero__actions-row">
                    <button type="button" class="cf-btn cf-btn--outline" data-add-to-cart="${recommended.variantId}"${recommended.available ? '' : ' disabled'}>
                      ${escapeHtml(recommended.available ? addToCartLabel : soldOutLabel)}
                    </button>
                    ${renderWishlistButton(recommended, {
                      inline: true,
                      ariaLabel: this.labels.addToWishlist || 'Zur Wunschliste hinzufügen',
                    })}
                  </div>
                </div>
              </div>
            </div>
          </article>

          <hr class="cf-results__divider" aria-hidden="true">

          <section class="cf-alts">
            <h3 class="cf-alts__title">${formatMultilineText(translationOrFallback(this.strings.otherOptions, 'Weitere Empfehlungen'))}</h3>
            <div class="cf-alts__list">${altHtml || `<p class="cf-alts__empty">${escapeHtml(this.labels.noAlternatives || '')}</p>`}</div>
          </section>

          ${renderResultBanners(this.banners)}
        </div>`;
    }

    bindResultsActions() {
      const addToCartLabel = themeProductString(this.strings, 'addToCart', 'addToCartButton', 'In den Warenkorb');

      this.resultsEl.querySelectorAll('[data-restart]').forEach((btn) => {
        if (btn.classList.contains('cf-btn--repeat')) {
          updateRepeatButtonLabel(this.strings, this.storeLocale, btn);
        } else {
          updateRestartButtonLabel(this.strings, btn);
        }
        btn.addEventListener('click', () => {
          this.resetToWizard();
        });
      });

      this.resultsEl.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const variantId = btn.dataset.addToCart;
          if (!variantId || btn.disabled) return;

          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');

          try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
            });

            if (response.ok) {
              const cartContent = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
              document.documentElement.dispatchEvent(
                new CustomEvent('cart:change', {
                  bubbles: true,
                  detail: { baseEvent: 'variant:add', cart: cartContent },
                })
              );
            } else {
              try {
                const responseJson = await response.json();
                if (responseJson.description) {
                  btn.textContent = responseJson.description;
                  setTimeout(() => {
                    btn.textContent = addToCartLabel;
                  }, 2000);
                }
              } catch {
                // Keep the current button label when the error response is not JSON.
              }
            }
          } catch {
            // Keep the current button label on network errors.
          } finally {
            btn.removeAttribute('aria-busy');
            btn.disabled = false;
          }
        });
      });

      this.bindWishlistActions();
    }

    loadHeroProductInfoText(product) {
      const container = this.resultsEl?.querySelector('[data-hero-product-info]');
      const prose = this.resultsEl?.querySelector('[data-hero-product-info-prose]');
      if (!container || !prose || !product?.url) return;

      fetchProductInfoText(product.url).then((html) => {
        if (!prose.isConnected) return;

        container.classList.remove('cf-hero__product-info--pending');
        container.style.visibility = '';

        if (html) {
          prose.innerHTML = html;
          return;
        }

        if (product.subtitle) {
          prose.textContent = product.subtitle;
          return;
        }

        container.hidden = true;
      });
    }

    bindWishlistActions() {
      const productById = new Map(this.products.map((product) => [Number(product.id), product]));

      this.resultsEl.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
        const product = productById.get(Number(btn.dataset.productId));
        if (!product) return;

        onSwymReady((swat) => syncWishlistButton(swat, product, btn));

        btn.addEventListener('click', () => {
          onSwymReady((swat) => toggleSwymWishlist(swat, product, btn));
        });
      });
    }
  }

  window.customElements.define('camera-finder', CameraFinder);
})();
